import React from 'react';
import {SelectionBox} from './Odds';
import {PeriodGroupedMarkets} from "../../../../markets/RequiredDisplayMarketTitles";
import {
    marketColTitlesByMarketGroup,
    MarketGroup,
    marketGroupFunctions,
    marketToRows,
    TableCell
} from "../../../../markets/marketMapping";
import {useDispatch, useSelector} from "react-redux";
import {
    ADD_COUPON_EVENT,
    CHANGE_NEED_CONFIRM_DIALOG_SETTINGS,
    getMarketLimitAsync,
    NEED_CONFIRM_COUPON_EVENT,
    REMOVE_COUPON_EVENT,
    UPDATE_COUPON_MESSAGE
} from "../../../../store/spirs/actions";
import {FixtureElement, MarketElement} from "../../../../models/api/codegen/code";
import {StoreType} from "../../../../store/spirs/types";
import {CouponEvent} from "../../../../store/spirs/reducers/couponReducer";
import {useRate} from "../../../FixtureVerbose/DefaultRate";
import {useTranslation} from "react-i18next";
import {sendFingerprint} from "../../../../utils/SendFingerprint";
import iziToast from "izitoast";
import {ReactComponent as LockIcon} from "./imgs/lock_icon_new1_filled.svg";
import {Mobile} from "../../../adaptive/Mobile";
import {Desktop} from "../../../adaptive/Desktop";
import {useBreakpoints} from "../../../../utils/useBreakpoints";
import {showCustomToast} from '../../../../utils/Toasts';
import clsx from "clsx";

type MarketsProps = {
    fixtureId: number;
    groupedMarkets: PeriodGroupedMarkets;
    marketGroupsToDisplay: MarketGroup[];
    competitionName: string;
    status: FixtureElement['s'];
    mobileMarketGroup: MarketGroup;
    suspended: boolean;
    hasOverlay: boolean;
};
export const Markets: React.FC<MarketsProps> = (props) => {
    const {t} = useTranslation();
    const {groupedMarkets, competitionName, fixtureId, marketGroupsToDisplay, status, mobileMarketGroup, hasOverlay} = props;
    const dispatch = useDispatch();
    const currentCouponType = useSelector((state: StoreType) => state.spirs.coupons.currentCouponType);
    const currentCouponEvents = useSelector((state: StoreType) => state.spirs.coupons[currentCouponType]);
    const needConfirm = useSelector((state: StoreType) => state.spirs.playerSettings.needConfirm);
    const rate = useRate(status);
    const settings = useSelector((state: StoreType) => state.spirs.playerSettings);
    const playerToken = useSelector((state: StoreType) => state.spirs.playerToken);

    const handleClickOddCell = React.useCallback(
        (tCell: TableCell, market: MarketElement) => (event: React.MouseEvent) => {
            if (hasOverlay) {
                return;
            }
            const alreadyAdded = currentCouponEvents.find(event => event.selectionId === tCell.selectionId);
            if (alreadyAdded) {
                dispatch(REMOVE_COUPON_EVENT(tCell.selectionId));
                return;
            }
            if (playerToken === 'GUEST' && currentCouponEvents.length >= 10) {
                showCustomToast(t('coupons.guestLimitReached'));
                return;
            }
            if (market.s !== 'ACTIVE' || tCell.isLabel || tCell.locked || props.suspended) {
                return;
            }
            if (needConfirm) {
                dispatch(CHANGE_NEED_CONFIRM_DIALOG_SETTINGS(event.currentTarget.id));
            }
            if ((currentCouponType === 'EXPRESS' || currentCouponType === 'SYSTEM')
                && currentCouponEvents.find((el: CouponEvent) => el.fixtureId === fixtureId)) {
                dispatch(UPDATE_COUPON_MESSAGE(t('coupons.alreadyExistFixtureMessage')));
            } else {
                dispatch(UPDATE_COUPON_MESSAGE(''));
                const couponEvent: CouponEvent = {
                    fixtureId: fixtureId,
                    selectionName: tCell.selectionName,
                    competitionName: competitionName,
                    selectionId: tCell.selectionId,
                    rate: rate,
                    active: true,
                    odds: Number(tCell.value),
                    marketName: market.n,
                    marketId: tCell.marketId,
                    minRate: market.mi,
                    maxRate: market.ma,
                    live: status === "LIVE"
                };
                if (settings.needConfirm) {
                    dispatch(NEED_CONFIRM_COUPON_EVENT(couponEvent));
                    dispatch(CHANGE_NEED_CONFIRM_DIALOG_SETTINGS(event.currentTarget.id));
                } else {
                    dispatch(ADD_COUPON_EVENT(couponEvent));
                }
                dispatch(getMarketLimitAsync.request({marketId: couponEvent.marketId, fixtureId: couponEvent.fixtureId}));
            }
        }, [currentCouponType, currentCouponEvents, rate, settings, hasOverlay]);

    const isSelect = (selectionId: number) => currentCouponEvents.map(s => s.selectionId).indexOf(selectionId) > -1;

    const mobileMarkets = groupedMarkets.groups.filter(g => g.group === mobileMarketGroup);
    const mobileMarket = mobileMarkets.length > 0
        ? mobileMarkets[0].groupedMarkets ? mobileMarkets[0].groupedMarkets.m[0] : null
        : null;
    const mobileGroup = marketToRows[mobileMarketGroup];

    return (<>
        <Desktop>
            <div className="flex gap-[10px]">
                {marketGroupsToDisplay.length === 1 && <EmptyMarket/>}
                {marketGroupsToDisplay.map((marketGroup, index) => {
                    const markets = groupedMarkets.groups.filter(g => g.group === marketGroup);
                    const market = markets.length > 0
                        ? markets[0].groupedMarkets ? markets[0].groupedMarkets.m[0] : null
                        : null;
                    const groups = marketToRows[marketGroup];
                    return (<Market key={index}
                                    group={marketGroup}
                                    market={market}
                                    groups={groups}
                                    handleClickOddCell={handleClickOddCell}
                                    isSelect={isSelect}
                                    suspended={props.suspended}
                                    hasOverlay={hasOverlay}
                    />)
                })}
            </div>
        </Desktop>
        <Mobile>
            <Market group={mobileMarketGroup}
                    market={mobileMarket}
                    groups={mobileGroup}
                    handleClickOddCell={handleClickOddCell}
                    isSelect={isSelect}
                    suspended={props.suspended}
                    hasOverlay={hasOverlay}
            />
        </Mobile>
    </>);
};

interface MarketProps {
    market: MarketElement | null | undefined;
    groups?: string[];
    group: MarketGroup;
    isSelect: (selectionId: number) => boolean;
    handleClickOddCell: (selection: TableCell, market: MarketElement) => (event: React.MouseEvent) => void;
    suspended: boolean;
    hasOverlay: boolean;
}

const Market: React.FC<MarketProps> = ({market, group, groups = [], isSelect, handleClickOddCell, suspended, hasOverlay}) => {
    return (<div
                style={{gridTemplateColumns: `repeat(${groups.length}, 1fr)`}}
                className={`tablet:w-[130px] desktop:w-[250px] grid gap-[2px] tablet:gap-[5px] text-[14px] `}
    >
        {!market

            ? groups.map((tableCellTitle, index) => (
                <div key={index} className={hasOverlay ? "opacity-25 pointer-events-none" : ""}>
                    <SelectionBox disabled={true} groups={groups} />
                </div>
            ))

            : marketGroupFunctions[group](market.sel, market.i).map((selection, index) => {

                return (<div key={index}
                             id={'selection-id-' + selection.selectionId}
                             onClick={handleClickOddCell(selection, market)}
                             className={hasOverlay ? "opacity-25 pointer-events-none" : ""}>
                    <SelectionBox
                        disabled={market.s !== 'ACTIVE' || suspended}
                        isSuspended={market.s === 'SUSPENDED' || suspended}
                        isLocked={selection.locked}
                        isLabel={selection.isLabel}
                        isSelected={isSelect(selection.selectionId)}
                        value={selection.value}
                        selectionName={marketColTitlesByMarketGroup(group)[index]}
                        groups={groups}
                    />
                </div>)

                }
            )
        }
    </div>)
};

export const ChessMarkets: React.FC<{ fixture: FixtureElement, hasOverlay: boolean }> = ({fixture, hasOverlay}) => {
    const {t, i18n: {language}} = useTranslation();
    const {isMobile} = useBreakpoints();
    const onClick = (actionLink?: string) => () => {
        if (hasOverlay) {
            return;
        }
        if (isMobile) {
            onClickMobile();
            return;
        }
        if (!fixture.a || !actionLink) {
            return;
        }
        sendFingerprint();
        actionLink && window.location.replace(actionLink + "/" + language);
    };

    const onClickMobile = () => {
        return iziToast.info({
            message: `
                    <div class="flex align-senter px-[20px] py-[15px] rounded-[4px]">
                        <div class="flex-shrink-0 mr-[15px]"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M12 0C18.6281 0 24 5.37187 24 12C24 18.6281 18.6281 24 12 24C5.37187 24 0 18.6281 0 12C0 5.37187 5.37187 0 12 0ZM9.67969 7.48828C9.21328 7.48828 8.83594 7.86562 8.83594 8.33203C8.83594 8.79844 9.21328 9.17578 9.67969 9.17578H11.0859V16.5371H9.08887C8.62268 16.5372 8.24533 16.9147 8.24512 17.3809C8.24512 17.8472 8.62255 18.2245 9.08887 18.2246H14.7705C15.2368 18.2245 15.6143 17.8472 15.6143 17.3809C15.614 16.9147 15.2367 16.5372 14.7705 16.5371H12.7734V8.33203C12.7734 7.86562 12.3961 7.48828 11.9297 7.48828H9.67969ZM11.9297 4.32422C11.4633 4.32422 11.0859 4.70156 11.0859 5.16797C11.0859 5.63438 11.4633 6.01172 11.9297 6.01172C12.3961 6.01172 12.7734 5.63438 12.7734 5.16797C12.7734 4.70156 12.3961 4.32422 11.9297 4.32422Z" fill="white" fill-opacity="0.65"/></svg>
                        </div>
                        <div class="text-[14px] font-normal">${t('chessLive.mobile_error')}</div>
                    </div>
            `,
            position: 'center',
            backgroundColor: '#383E45',
            messageColor: 'rgba(255, 255, 255, 0.85)',
            messageSize: '14px',
            maxWidth: 414,
            progressBar: false,
            timeout: 3000,
            onOpening: () => {
                const toast = document.querySelector('.iziToast') as HTMLElement;
                const toastBody = document.querySelector('.iziToast-body') as HTMLElement;
                const toastClose = document.querySelector('.iziToast-close') as HTMLElement;
                toastClose.style.display = "none";
                toastBody.style.padding = '0';
                toast.style.zIndex = "10000";
                toast.style.position = "fixed";
                toast.style.top = "100px";
                toast.style.left = "50%";
                toast.style.transform = "translate(-50%, 0)";
            },
            closeOnClick: true
        });
    };

    const chessMarketClass = "text-[#a5a4a4] text-bold bg-[#121518] cursor-not-allowed tablet:h-[40px] flex items-center justify-center border border-[#3A424A] shrink-0 tablet:min-w-[150px] max-tablet:w-[60px] max-tablet:h-[32px] " + (hasOverlay ? "opacity-25 pointer-events-none" : "");

    if (fixture.gm.length === 0 || fixture.gm[0].m.length === 0) {
        return (<>
            {[1, 2, 3, 4, 5].map(k => <div className={chessMarketClass} key={k}/>)}
        </>)
    }
    const EmptyMarketsLeft = () => fixture.gm.length >= 0 && fixture.gm[0].m[0].sel.length === 1
        ? (<>{[1, 2].map(k => <div style={{color:"#fff"}} className={chessMarketClass} key={k}>-</div>)}</>)
        : <></>;
    const EmptyMarketsRight = () => fixture.gm.length >= 0 && fixture.gm[0].m[0].sel.length === 1
        ? (<>{[4, 5].map(k => <div style={{color:"#fff"}} className={chessMarketClass} key={k}>-</div>)}</>)
        : <></>;
    const Lock = () => !fixture.a ? (
        <div className="mr-[6px] desktop:mr-[10px]">
            <LockIcon/>
        </div>
    ) : <></>;
    return <div className="flex flex-wrap gap-[5px] justify-end">
        {fixture.gm.map((gr, i) => (<React.Fragment key={i}>
            {gr.m.map(market => (<React.Fragment key={market.i}>
                <EmptyMarketsLeft/>
                {market.sel.map(selection => (
                    <div
                        onClick={onClick(selection.al)}
                        key={selection.i}
                        className={clsx(
                            "max-tablet:w-[60px] max-tablet:h-[32px] bg-[#3A424A] tablet:h-[40px] flex items-center justify-center border border-[#3A424A] shrink-0 tablet:min-w-[150px] text-base",
                            fixture.a ? "text-[#fff] cursor-pointer max-tablet:h-32" : "text-[#a5a4a4] cursor-not-allowed",
                            hasOverlay ? "opacity-25 pointer-events-none" : ""
                        )}>
                        <Lock/>
                        {selection.n}
                    </div>
                ))}
                <EmptyMarketsRight/>
            </React.Fragment>))}
        </React.Fragment>))}
    </div>
};

const EmptyMarket = React.memo(() => {
    return (
        <div className="h-full bg-[#171A1E]"/>
    )
}, () => true);
